/**
 * settlement_lod — the settlement REPRESENTATION LADDER by distance, with a
 * closing-speed lookahead (fast-approach wave, 2026-09-02).
 *
 * A city took 12–20 s to assemble once its build radius was crossed, and the
 * old radius (1.3 km on medium) is 9 s at hovercraft cruise: you arrived while
 * the far-tier ring was still standing in for the city ("I can move to a city
 * and it is just at HLOD-0 still"). Two changes, both pure so the harness pins
 * them:
 *   1. The ladder is evaluated on the EFFECTIVE distance — the distance minus
 *      the closing speed × the build-time budget — so every level triggers as
 *      early as the approach speed demands. Standing still, nothing changes.
 *   2. Every band is wider, and there are more of them: the far ring now has
 *      a LOD2 band and a LOD3 band (the LOD3 files are ~5 % of LOD0) reaching
 *      1.5× the draw distance, so a city refines step by step instead of
 *      jumping from silhouette boxes to the full build.
 *
 * Ladder, inside → out (all thresholds on the effective distance except
 * `dispose`, which uses the raw distance so a fast pass never thrashes):
 *   LOD0 real build   < near      full-detail prefabs + trimesh colliders
 *   LOD1 real build   < build     simplified prefabs + box colliders
 *   warm ring         < warm      plan + civ pack prefetched
 *   HLOD-0 LOD2       < hlod2     far ring instancing the LOD2 prefabs
 *   HLOD-0 LOD3       < hlod      far ring instancing the LOD3 prefabs
 *   HLOD-1 boxes      < boxes     silhouette ladders (6 bands), 2× the draw distance, 4–12 km
 *   nothing           beyond
 */
import type { SettlementTier } from '../../../shared/settlements.js';

/** Seconds of approach the ladder anticipates: the measured cold assembly of a city. */
export const LOOKAHEAD_S = 15;
/** Closing speed the lookahead saturates at (m/s): hovercraft boost. Space/warp flight is excluded upstream. */
export const LOOKAHEAD_MAX_SPEED = 320;
/** Stream-out hysteresis (m). */
export const HYSTERESIS_M = 40;
/** Warm ring outside the build radius (plan + civ pack prefetch). */
export const WARM_RING_SCALE = 1.3;
/** Build-in radius per tier at scale 1 (the pre-wave RENDER_RADIUS). */
export const BUILD_RADIUS: Record<SettlementTier, number> = { city: 1000, town: 650, hamlet: 300, capital: 4500, district: 700, circuit: 1500 };
/** Share of the draw distance a tier builds in at (a city's build radius is ¾ of the draw distance on the wide presets). */
export const BUILD_DRAW_SHARE: Record<SettlementTier, number> = { city: 0.75, town: 0.6, hamlet: 0.5, capital: 0.9, district: 0.5, circuit: 0.6 };
/** LOD0 reach cap per tier (m): a capital district goes full-detail only where you stand — thirty of them share one skyline (capital wave 2026-09-15). */
export const NEAR_CAP: Record<SettlementTier, number> = { city: Infinity, town: Infinity, hamlet: Infinity, capital: Infinity, district: 450, circuit: Infinity };

export interface SettlementBands {
  /** LOD0 (full detail) inside this; 0 on forceLowLod tiers (never LOD0). */
  near: number;
  /** The real (LOD1) build triggers inside this. */
  build: number;
  /** Assets prefetch inside this. */
  warm: number;
  /** HLOD-0 instances LOD2 inside this (LOD3 beyond, out to `hlod`). */
  hlod2: number;
  /** HLOD-0 reach; silhouette boxes beyond, out to `boxes`. */
  hlod: number;
  /** Silhouette-box reach — NOTHING is drawn for a settlement beyond this. */
  boxes: number;
  /** A built settlement disposes beyond this (raw distance). */
  dispose: number;
}

/** Closing speed toward a point (m/s, ≥ 0): the velocity component along the direction to it. */
export function closingSpeed(vx: number, vz: number, dx: number, dz: number): number {
  const d = Math.hypot(dx, dz);
  if (d < 1e-6) return 0;
  return Math.max(0, (vx * dx + vz * dz) / d);
}

/** Distance minus the anticipated approach over `lookaheadS`, never below 0. */
export function effectiveDistance(dist: number, closing: number, lookaheadS = LOOKAHEAD_S): number {
  const v = Math.min(LOOKAHEAD_MAX_SPEED, Math.max(0, closing));
  return Math.max(0, dist - v * lookaheadS);
}

/**
 * The bands for a tier at a quality preset. `drawDist` is the preset's draw
 * distance (chunkRadius × 256 m), `radiusScale` its settlementRadiusScale.
 */
export function settlementBands(tier: SettlementTier, drawDist: number, radiusScale: number, forceLowLod: boolean, distantDetail = true): SettlementBands {
  const base = BUILD_RADIUS[tier] * radiusScale;
  // Build in at the larger of the tier radius and a share of the draw
  // distance (¾ for a city, less for the small tiers): the far ring already
  // draws the same silhouettes there, so building earlier costs colliders
  // and dressing, not triangles.
  const build = Math.max(base, BUILD_DRAW_SHARE[tier] * drawDist);
  // LOD0 reach: 40 % of the draw distance, floored at 600 m (was 30 %, 400 m).
  const near = forceLowLod ? 0 : Math.min(NEAR_CAP[tier], Math.max(600, Math.min(2000, 0.4 * drawDist)));
  const warm = build * WARM_RING_SCALE;
  // The far ring: LOD2 out to the draw distance (≥ 1.5 km), LOD3 to 1.5× it (≥ 3 km, ≤ 9 km).
  const hlod = Math.max(3000, Math.min(9000, 1.5 * drawDist));
  const hlod2 = forceLowLod ? 0 : Math.max(1500, Math.min(4000, drawDist));
  const dispose = Math.max(build, drawDist) + HYSTERESIS_M;
  // The silhouette tier is BOUNDED (user 2026-09-02 evening: a landmark's box
  // from kilometres away stood across the whole horizon): twice the draw
  // distance, 4–12 km, then nothing.
  const boxes = Math.max(4000, Math.min(12000, 2 * drawDist));
  if (!distantDetail) {
    // Distant detail OFF (2026-09-08): nothing past the draw distance — no far
    // ring, no silhouettes; a settlement builds only INSIDE the window (the
    // tier radius no longer reaches past it) and streams out just beyond.
    const buildIn = Math.min(build, drawDist);
    return { near: Math.min(near, buildIn), build: buildIn, warm: Math.min(warm, drawDist + HYSTERESIS_M), hlod2: 0, hlod: 0, boxes: 0, dispose: drawDist + HYSTERESIS_M };
  }
  return { near, build, warm, hlod2, hlod, boxes, dispose };
}

/** HLOD-0 prefab level for a site at effective distance `eff`: 2 inside the LOD2 band, else 3. */
export function hlodLodFor(eff: number, bands: SettlementBands): 2 | 3 {
  return eff < bands.hlod2 ? 2 : 3;
}

/**
 * Texture cap for the LOD0 files the per-building refinement loads (R8):
 * 1024 px on every preset but ultra — a building spanning 35–100 % of a
 * 1080p viewport is ~1:1 texel-per-pixel at 1024, and the 2048 civ-B LOD0
 * files (18–21 MB each) were the "takes a while from LOD1 to LOD0".
 */
export function refineTextureCap(presetMaxTextureRes: number, radiusScale: number): number {
  return radiusScale >= 2.5 ? presetMaxTextureRes : Math.min(presetMaxTextureRes, 1024);
}

// ── R8: screen-space LOD selection with a downgrade path ────────────────────
// Per BUILDING, not per settlement: the level is chosen from the fraction of
// the viewport height the building spans (the 3D Tiles / Unreal rule), with
// a hysteresis band so a building at a boundary never flickers, and it goes
// DOWN as well as up — an instancer batch swap, never a rebuild.

/** Fraction of the viewport height a `height` m object spans at `dist` m (vertical fov in radians). */
export function screenSize(height: number, dist: number, fovRad: number): number {
  return height / (Math.max(1, dist) * 2 * Math.tan(fovRad / 2));
}

/** Upgrade thresholds (fraction of viewport height): LOD0 ≥ [0], LOD1 ≥ [1], LOD2 ≥ [2], else LOD3. */
export const SCREEN_LOD_THRESHOLDS: readonly [number, number, number] = [0.35, 0.10, 0.035];
/** A level is only given up once the size drops below threshold × this (Unreal's LODHysteresis idea, wider). */
export const SCREEN_LOD_HYSTERESIS = 0.7;

/**
 * Detail bias from the preset's settlementRadiusScale (potato 0.6, low 1,
 * med 1.3, high 2, ultra 2.5): thresholds are DIVIDED by it (bigger = more
 * detail sooner), medium = 1. forceLowLod tiers also never reach LOD0.
 */
export function screenLodBias(radiusScale: number): number {
  return Math.max(0.45, Math.min(2, radiusScale / 1.3));
}

/** The level a building should show at screen size `ssize`, given the level it shows now. */
export function screenLodFor(ssize: number, current: 0 | 1 | 2 | 3, bias: number, forceLowLod: boolean): 0 | 1 | 2 | 3 {
  const b = Math.max(0.05, bias);
  let target: 0 | 1 | 2 | 3 = 3;
  for (let l = 0; l < 3; l++) {
    const thr = SCREEN_LOD_THRESHOLDS[l] / b;
    // Keep the current level (or better) while above its hysteresis floor.
    const keep = current <= l ? thr * SCREEN_LOD_HYSTERESIS : thr;
    if (ssize >= keep) { target = l as 0 | 1 | 2; break; }
  }
  if (forceLowLod && target === 0) target = 1;
  return target;
}

/** R9: load-queue priority — bigger on screen first; a predicted size (at the lookahead position) ranks above the current one. */
export function loadPriority(ssizeNow: number, ssizePredicted: number): number {
  return Math.max(ssizeNow, ssizePredicted);
}

// ── S7: adaptive swap pacing + a minimum dwell ──────────────────────────────
/** Frame time (ms) above which a tick performs NO swaps, unless the device's own baseline is slower still. */
export const SWAP_FRAME_CAP_MS = 20;
/** Frame time (ms) above which the swap budget halves (same baseline rule). */
export const SWAP_FRAME_SOFT_MS = 12;
/** A building is not re-swapped within this long of its last change (no oscillation at a hysteresis edge under motion). */
export const SWAP_DWELL_MS = 2000;
/** If nothing has swapped for this long while candidates wait, one swap goes through regardless of pacing. */
export const SWAP_STARVE_MS = 1000;

/**
 * Swaps allowed this tick. The caps are RELATIVE to the device's baseline
 * frame time (an EMA): a spike to 1.5× the baseline (or past 20 ms, whichever
 * is later) means no swaps, 1.2× (or past 12 ms) halves the budget — a 30 fps
 * machine at its usual 33 ms is NOT late, it is normal, and a fixed 20 ms
 * cap starved it forever (probe-confirmed under the software renderer).
 */
export function swapBudget(lastFrameMs: number, base: number, baselineMs = 16.7): number {
  if (!(lastFrameMs >= 0)) return base;
  const bl = baselineMs > 0 ? baselineMs : 16.7;
  if (lastFrameMs > Math.max(SWAP_FRAME_CAP_MS, 1.5 * bl)) return 0;
  if (lastFrameMs > Math.max(SWAP_FRAME_SOFT_MS, 1.2 * bl)) return Math.max(1, Math.floor(base / 2));
  return base;
}

/** May this building swap now? Never within the dwell of its last swap (0 = never swapped). */
export function dwellOk(now: number, lastSwapAt: number, dwellMs = SWAP_DWELL_MS): boolean {
  return lastSwapAt <= 0 || now - lastSwapAt >= dwellMs;
}
